// src/pages/public/Webinars/WebinarsPage.jsx
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { FaArrowRight, FaCalendarAlt, FaUsers, FaRegCalendarCheck, FaHistory } from 'react-icons/fa'
import { FiCalendar, FiClock, FiUsers } from 'react-icons/fi'

import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch.js'
import { fetchWebinars } from '../../../redux/slices/webinarsSlice.js'
import { fetchPageBySlug } from '../../../redux/slices/cmsSlice.js'
import SEO from '../../../components/seo/SEO.jsx'
import { formatDateTime, isFutureDate } from '../../../utils/formatDate.js'
import { LazyImage } from '../../../components/common/LazyImage.jsx'
import EmptyState from '../../../components/common/EmptyState.jsx'
import SkeletonCard from '../../../components/common/SkeletonCard.jsx'
import LoadMoreButton from '../../../components/common/LoadMoreButton.jsx'

import WebinarImage from '../../../assets/Images/webinar/image 121.png'
import './WebinarsPage.scss'

export default function WebinarsPage() {
  const dispatch = useAppDispatch()

  const { currentPage: webinarsPage, loading: pageLoading } = useAppSelector(
    (state) => state.cms
  )
  const { webinars, loading, hasMore } = useAppSelector(
    (state) => state.webinars
  )

  const [activeTab, setActiveTab] = useState('upcoming')

  useEffect(() => {
    dispatch(fetchWebinars({ page: 1, limit: 10 }))
    dispatch(fetchPageBySlug('webinars'))
  }, [dispatch])

  const upcomingWebinars = (webinars || []).filter((w) =>
    isFutureDate(w.dateTime)
  )
  const pastWebinars = (webinars || []).filter(
    (w) => !isFutureDate(w.dateTime)
  )

  const totalUpcoming = upcomingWebinars.length
  const totalPast = pastWebinars.length

  // Get the first upcoming webinar for the hero section
  const featuredWebinar = upcomingWebinars.length > 0
    ? upcomingWebinars[0]
    : (webinars.length > 0 ? webinars[0] : null)

  // Prepare hero data from the featured webinar
  const heroTitle = featuredWebinar?.title || 'Webinars & Events'
  const heroSubtitle = featuredWebinar?.hero?.subheading || webinarsPage?.subheading || 'Join our expert-led sessions on digital transformation'
  const heroImage = featuredWebinar?.hero?.imageUrl || webinarsPage?.heroImageUrl || WebinarImage
  const heroDateTime = featuredWebinar?.dateTime ? formatDateTime(featuredWebinar.dateTime) : 'Coming Soon'
  const heroDuration = featuredWebinar?.duration || '60 min'
  const heroAttendees = featuredWebinar?.attendees ||
    (featuredWebinar?.speakers?.length
      ? `${featuredWebinar.speakers.length} Speaker${featuredWebinar.speakers.length > 1 ? 's' : ''}`
      : 'Live Online')
  const heroButtonText = featuredWebinar ? 'Register Now' : 'View All Events'
  const heroButtonLink = featuredWebinar ? `/webinars/${featuredWebinar.slug}` : '#webinars-list'

  const stats = [
    {
      icon: <FiCalendar />,
      text: heroDateTime
    },
    {
      icon: <FiClock />,
      text: heroDuration
    },
    {
      icon: <FiUsers />,
      text: heroAttendees
    }
  ]

  // Skeleton while CMS is loading
  if (pageLoading && !webinarsPage) {
    return (
      <>
        <SEO
          title="Webinars & Events"
          description="Join our upcoming webinars and access recordings of past events. Learn about the latest trends in digital transformation."
        />
        <div className="space-y-12">
          <section className="bg-gray-50">
            <div className="container-custom section-padding">
              <div className="max-w-3xl animate-pulse">
                <div className="h-12 bg-black-200 rounded w-1/2 mb-6"></div>
                <div className="h-6 bg-black-200 rounded w-3/4 mb-8"></div>
                <div className="h-48 bg-black-200 rounded-lg"></div>
              </div>
            </div>
          </section>

          <section className="bg-white">
            <div className="container-custom">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[...Array(4)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            </div>
          </section>
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title={webinarsPage?.seoTitle || 'Webinars & Events'}
        description={
          webinarsPage?.seoDescription ||
          'Join our upcoming webinars and access recordings of past events. Learn about the latest trends in digital transformation.'
        }
      />

      <div className="webinars-page-root">
        {/* ====================
            HERO – v1 style
           ==================== */}
        <section className="webinar-listing-hero">
          <div className="webinar-listing-container">
            <div className="webinar-listing-grid">
              {/* Left – Content */}
              <motion.div
                className="content-column"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.span
                  className="webinar-badge"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <span className="badge-icon">
                    <FaCalendarAlt />
                  </span>
                  Next Event :
                </motion.span>

                <motion.h1
                  className="webinar-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  {heroTitle}
                </motion.h1>

                {/* Stats */}
                <motion.div
                  className="webinar-stats"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {stats.map((stat, index) => (
                    <motion.span
                      key={index}
                      className="stat-item"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    >
                      <span className="stat-icon">{stat.icon}</span>
                      {stat.text}
                    </motion.span>
                  ))}
                </motion.div>

                {/* Description */}
                <motion.p
                  className="webinar-description"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  {heroSubtitle}
                </motion.p>

                {/* Button */}
                <motion.a
                  href={heroButtonLink}
                  className="webinar-button"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  whileHover={{
                    scale: 1.05,
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  {heroButtonText}
                  <FaArrowRight className="button-icon" />
                </motion.a>
              </motion.div>

              {/* Right – Image */}
              <motion.div
                className="image-column"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <motion.img
                  src={heroImage}
                  alt="Webinar series"
                  className="webinar-image"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ====================
            LIST – v1 tabbed style
           ==================== */}
        <WebinarsListSection
          webinars={webinars}
          loading={loading}
          hasMore={hasMore}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          upcomingWebinars={upcomingWebinars}
          pastWebinars={pastWebinars}
          onLoadMore={() => {
            const nextPage = Math.ceil(webinars.length / 10) + 1
            dispatch(fetchWebinars({ page: nextPage, limit: 10 }))
          }}
        />
      </div>
    </>
  )
}

/* ================================
   List Section (tabbed upcoming/past)
   ================================ */

function WebinarsListSection({
  webinars,
  loading,
  hasMore,
  activeTab,
  setActiveTab,
  upcomingWebinars,
  pastWebinars,
  onLoadMore,
}) {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { amount: 0.2 })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        when: 'beforeChildren',
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  }

  const currentList =
    activeTab === 'upcoming' ? upcomingWebinars : pastWebinars

  return (
    <section className="webinar-section" id="webinars-list" ref={sectionRef}>
      <div className="container">
        {/* Switch Tabs */}
        <motion.div
          className="webinar-switch"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <button
            className={`switch-button ${activeTab === 'upcoming' ? 'active' : ''
              }`}
            onClick={() => setActiveTab('upcoming')}
          >
            <FaRegCalendarCheck className="switch-icon" />
            Upcoming Webinars
          </button>
          <button
            className={`switch-button ${activeTab === 'past' ? 'active' : ''
              }`}
            onClick={() => setActiveTab('past')}
          >
            <FaHistory className="switch-icon" />
            Past Webinars
          </button>
        </motion.div>

        {/* Loading state */}
        {loading && webinars.length === 0 ? (
          <div className="webinar-grid">
            {[...Array(4)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : currentList.length > 0 ? (
          <>
            <motion.div
              className="webinar-grid"
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              variants={containerVariants}
              key={activeTab}
            >
              {currentList.map((webinar) => (
                <motion.div
                  key={webinar._id}
                  className="webinar-card"
                  variants={itemVariants}
                >
                  <WebinarCard webinar={webinar} isUpcoming={isFutureDate(webinar.dateTime)} />
                </motion.div>
              ))}
            </motion.div>

            <LoadMoreButton
              hasMore={hasMore}
              loading={loading}
              onLoadMore={onLoadMore}
            />
          </>
        ) : (
          <div className="webinar-empty-wrapper">
            <EmptyState
              title={
                activeTab === 'upcoming'
                  ? 'No Upcoming Webinars'
                  : 'No Past Webinars'
              }
              message={
                activeTab === 'upcoming'
                  ? "We're planning new webinars. Check back soon or subscribe to our newsletter for updates."
                  : 'Our webinar recordings will be available here after each event.'
              }
            />
          </div>
        )}
      </div>
    </section>
  )
}

/* ================================
   Webinar Card – dark v1 style
   ================================ */

function WebinarCard({ webinar, isUpcoming }) {
  const primarySpeaker = webinar.speaker
  const attendeesText =
    webinar.attendees ||
    (webinar.speakers?.length
      ? `${webinar.speakers.length} Speaker${webinar.speakers.length > 1 ? 's' : ''}`
      : 'Live online')

  return (
    <div className="webinar-card-inner">
      <div className="card-image">
        <LazyImage
          src={webinar?.hero?.imageUrl}
          alt={webinar.title}
          className="w-full h-full object-cover"
          placeholder={
            <div className="w-full h-full bg-black-200" />
          }
        />
      </div>

      <div className="card-content">
        <h3>{webinar.title}</h3>

        <p className="subheading">
          {webinar.hero?.subheading}
        </p>

        <div className="details-row">
          <div className="detail-item">
            <FiCalendar className="icon" />
            <span>{formatDateTime(webinar.dateTime)}</span>
          </div>
          {webinar.duration && (
            <div className="detail-item">
              <FiClock className="icon" />
              <span>{webinar.duration}</span>
            </div>
          )}
          {attendeesText && (
            <div className="detail-item">
              <FiUsers className="icon" />
              <span>{attendeesText}</span>
            </div>
          )}
        </div>

        <div className="speaker-row">
          <div className="speaker-info">
            {primarySpeaker && (
              <div>
                <span className="speaker-name">{primarySpeaker.name}</span>
                <span className="speaker-desc">
                  {primarySpeaker.companyPosition || primarySpeaker.role}
                </span>
              </div>
            )}
          </div>

          <Link to={`/webinars/${webinar.slug}`} className="register-btn">
            {isUpcoming ? 'Register Now' : 'Watch Recording'}
          </Link>
        </div>
      </div>
    </div>
  )
}